// ResizablePanel — horizontal split with draggable divider.
// Used in T1 (Tree + List) layout for the register tree sidebar.
// Pure CSS resize — no external dependencies.

import { useRef, useCallback, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface ResizablePanelProps {
  /** Content for the left (sidebar) panel. */
  left: ReactNode
  /** Content for the right (main) panel. */
  right: ReactNode
  /** Initial width of the left panel in pixels. Default: 280. */
  defaultWidth?: number
  /** Minimum width of the left panel. Default: 180. */
  minWidth?: number
  /** Maximum width of the left panel. Default: 480. */
  maxWidth?: number
  className?: string
}

export function ResizablePanel({
  left,
  right,
  defaultWidth = 280,
  minWidth = 180,
  maxWidth = 480,
  className,
}: ResizablePanelProps) {
  const [width, setWidth] = useState(defaultWidth)
  const dragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      dragging.current = true

      const startX = e.clientX
      const startWidth = width

      const handleMouseMove = (e: MouseEvent) => {
        if (!dragging.current) return
        const delta = e.clientX - startX
        const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta))
        setWidth(newWidth)
      }

      const handleMouseUp = () => {
        dragging.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [width, minWidth, maxWidth],
  )

  return (
    <div ref={containerRef} className={cn('flex h-full overflow-hidden', className)}>
      {/* Left panel */}
      <div className="flex-shrink-0 overflow-y-auto border-r bg-muted/30" style={{ width }}>
        {left}
      </div>

      {/* Drag handle */}
      <div
        className="w-1 flex-shrink-0 cursor-col-resize transition-colors hover:bg-primary/20 active:bg-primary/30"
        onMouseDown={handleMouseDown}
      />

      {/* Right panel */}
      <div className="min-w-0 flex-1 overflow-y-auto">{right}</div>
    </div>
  )
}
